/**
 * Cart Total Offer Manager Component for Focal Theme
 * Manages single threshold cart offers with product selection
 * Version: 1.0.0
 * 
 * Usage:
 * <cart-total-offer-manager>
 *   <script type="application/json">
 *     // Offer configuration from metaobjects
 *   </script>
 * </cart-total-offer-manager>
 */

// Global state for cart total offers
window.CartTotalOfferGlobal = window.CartTotalOfferGlobal || {
  isProcessing: false,
  lastProcessTime: 0,
  isRefreshing: false,
  loadingVisible: false
};

class CartTotalOfferManager extends HTMLElement {
  constructor() {
    super();
    
    this.THROTTLE_DELAY = 500;
    this.offerConfig = null;
    this.OFFER_PROPERTY_KEY = '_CartTotalOffer';
    
    // Bind methods
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
  }

  connectedCallback() {
    console.log('[CartTotalOffer] 🎯 Component connected');
    
    // Ensure loading indicator exists
    this.ensureLoadingIndicator();
    
    // Parse offer configuration
    this.parseOfferConfig();
    
    if (!this.offerConfig) {
      console.warn('[CartTotalOffer] ⚠️ No offer configured');
      return;
    }

    console.log('[CartTotalOffer] 📋 Offer configuration:', {
      title: this.offerConfig.title,
      threshold: this.offerConfig.tier_limit,
      autoAdd: this.offerConfig.auto_add,
      productCount: this.getProductList().length
    });

    // Listen to cart updates
    document.documentElement.addEventListener('cart:updated', this.handleCartUpdate);
    
    console.log('[CartTotalOffer] ✓ Event listener attached');
    
    // Initial check with proper DOM/cart readiness check
    this.safeInitialize();
  }

  disconnectedCallback() {
    document.documentElement.removeEventListener('cart:updated', this.handleCartUpdate);
    console.log('[CartTotalOffer] Component disconnected');
  }

  /**
   * Parse offer configuration from JSON script tag
   */
  parseOfferConfig() {
    const configScript = this.querySelector('script[type="application/json"]');
    
    if (!configScript) {
      console.error('[CartTotalOffer] ❌ No config script found');
      return;
    }

    try {
      const data = JSON.parse(configScript.textContent);
      this.offerConfig = data.offers && data.offers.length > 0 ? data.offers[0] : null;
      
      console.log('[CartTotalOffer] ✅ Config loaded:', this.offerConfig);
    } catch (e) {
      console.error('[CartTotalOffer] ❌ Failed to parse config:', e);
    }
  }

  /**
   * Get list of product variant IDs from offer config
   */
  getProductList() {
    if (!this.offerConfig) return [];
    
    const products = [];
    Object.keys(this.offerConfig).forEach(key => {
      if (key.startsWith('products_list_')) {
        products.push(this.offerConfig[key]);
      }
    });
    
    return products;
  }

  /**
   * Safe initialization with DOM and cart readiness checks
   */
  safeInitialize() {
    // Wait for DOM to be fully ready
    if (document.readyState === 'loading') {
      console.log('[CartTotalOffer] ⏳ Waiting for DOM to be ready...');
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeWithRetry();
      });
    } else {
      // DOM already ready, initialize with retry logic
      this.initializeWithRetry();
    }
  }

  /**
   * Initialize with retry logic for first page load
   */
  async initializeWithRetry(attempt = 1, maxAttempts = 3) {
    const delay = attempt === 1 ? 500 : 1000; // 500ms first try, 1s for retries
    
    console.log(`[CartTotalOffer] 🔄 Initialize attempt ${attempt}/${maxAttempts} (delay: ${delay}ms)`);
    
    await new Promise(resolve => setTimeout(resolve, delay));
    
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      
      // Verify cart is valid
      if (!cart || typeof cart.items === 'undefined') {
        throw new Error('Invalid cart data');
      }
      
      console.log('[CartTotalOffer] ✅ Cart data loaded successfully');
      await this.processOffer(cart);
      
    } catch (error) {
      console.error(`[CartTotalOffer] ❌ Error on attempt ${attempt}:`, error);
      
      // Retry if we haven't exceeded max attempts
      if (attempt < maxAttempts) {
        console.log(`[CartTotalOffer] 🔄 Retrying... (${attempt + 1}/${maxAttempts})`);
        await this.initializeWithRetry(attempt + 1, maxAttempts);
      } else {
        console.error('[CartTotalOffer] ❌ Failed to initialize after all attempts');
        this.hideLoading();
      }
    }
  }

  /**
   * Initialize with current cart state (legacy - kept for compatibility)
   */
  async initializeWithCurrentCart() {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      await this.processOffer(cart);
    } catch (error) {
      console.error('[CartTotalOffer] ❌ Error initializing:', error);
    }
  }

  /**
   * Handle cart update events
   */
  async handleCartUpdate(event) {
    const global = window.CartTotalOfferGlobal;
    const now = Date.now();
    
    // Throttle processing
    if (now - global.lastProcessTime < this.THROTTLE_DELAY) {
      console.log('[CartTotalOffer] ⏭️ Throttling...');
      return;
    }
    
    if (global.isProcessing) {
      console.log('[CartTotalOffer] ⏭️ Already processing...');
      return;
    }

    global.lastProcessTime = now;
    
    const cart = event.detail?.cart;
    if (!cart) {
      console.warn('[CartTotalOffer] ⚠️ No cart data in event');
      return;
    }
    
    await this.processOffer(cart);
  }

  /**
   * Main offer processing logic
   */
  async processOffer(cart) {
    const global = window.CartTotalOfferGlobal;
    
    if (global.isProcessing) {
      return;
    }

    // Show loading IMMEDIATELY
    this.showLoading();
    
    global.isProcessing = true;
    console.log('[CartTotalOffer] 🔄 Processing offer...');

    try {
      // Calculate cart total excluding offer products
      const adjustedTotal = this.calculateAdjustedTotal(cart);
      const threshold = this.offerConfig.tier_limit;
      
      console.log('[CartTotalOffer] 💰 Cart total:', {
        adjustedTotal: adjustedTotal.toFixed(2),
        threshold,
        qualified: adjustedTotal >= threshold
      });

      // Check if offer product already in cart
      const offerProductInCart = this.hasOfferProduct(cart);
      
      console.log('[CartTotalOffer] 🎁 Offer product status:', {
        inCart: offerProductInCart,
        autoAdd: this.offerConfig.auto_add,
        autoAddType: typeof this.offerConfig.auto_add,
        propertyName: this.offerConfig.propery_name
      });

      // Decision logic
      if (offerProductInCart && adjustedTotal < threshold) {
        // Remove offer product if below threshold
        console.log('[CartTotalOffer] 🗑️ Removing offer product (below threshold)');
        
        // Set refreshing flag BEFORE removing to prevent flicker
        global.isRefreshing = true;
        
        await this.removeOfferProduct(cart);
        return;
      }

      if (!offerProductInCart && adjustedTotal >= threshold && this.offerConfig.auto_add) {
        // Auto-add first product if threshold met
        console.log('[CartTotalOffer] ➕ Auto-adding offer product');
        
        // Set refreshing flag BEFORE adding to prevent flicker
        global.isRefreshing = true;
        
        const products = this.getProductList();
        if (products.length > 0) {
          await this.addOfferProduct(products[0]);
        }
        return;
      }

      // Clear refresh flag if no cart modification
      global.isRefreshing = false;

      // Dispatch event for UI update
      this.dispatchOfferUpdateEvent(adjustedTotal, threshold, offerProductInCart);
      
      // Hide loading after a short delay to ensure UI has updated
      setTimeout(() => this.hideLoading(), 500);

    } catch (error) {
      console.error('[CartTotalOffer] ❌ Error processing offer:', error);
      this.hideLoading();
    } finally {
      global.isProcessing = false;
      console.log('[CartTotalOffer] ✅ Processing complete');
    }
  }

  /**
   * Calculate cart total excluding offer products
   */
  calculateAdjustedTotal(cart) {
    if (!cart || !cart.items) return 0;
    
    return cart.items.reduce((total, item) => {
      const isOfferProduct = 
        (item.properties && item.properties[this.offerConfig.propery_name]) ||
        (item.properties && item.properties[this.OFFER_PROPERTY_KEY]);
      
      return isOfferProduct ? total : total + item.final_line_price;
    }, 0) / 100;
  }

  /**
   * Check if offer product is in cart
   */
  hasOfferProduct(cart) {
    if (!cart || !cart.items) return false;
    
    return cart.items.some(item =>
      (item.properties && item.properties[this.offerConfig.propery_name]) ||
      (item.properties && item.properties[this.OFFER_PROPERTY_KEY])
    );
  }

  /**
   * Add offer product to cart
   */
  async addOfferProduct(variantId) {
    try {
      console.log('[CartTotalOffer] 📡 Adding product:', variantId);
      
      // Use propery_name from config, fallback to default key
      const propertyKey = this.offerConfig.propery_name || this.OFFER_PROPERTY_KEY;
      
      console.log('[CartTotalOffer] 🏷️ Using property key:', propertyKey);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: {
              [propertyKey]: 'true'
            }
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add product');
      }

      console.log('[CartTotalOffer] ✅ Product added');
      
      // Refresh cart UI
      await this.refreshCartUI();
      
    } catch (error) {
      console.error('[CartTotalOffer] ❌ Error adding product:', error);
    }
  }

  /**
   * Remove offer product from cart
   */
  async removeOfferProduct(cart) {
    try {
      const offerItem = cart.items.find(item =>
        (item.properties && item.properties[this.offerConfig.propery_name]) ||
        (item.properties && item.properties[this.OFFER_PROPERTY_KEY])
      );

      if (!offerItem) {
        console.log('[CartTotalOffer] ℹ️ No offer product to remove');
        return;
      }

      console.log('[CartTotalOffer] 📡 Removing product:', offerItem.key);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: offerItem.key,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove product');
      }

      console.log('[CartTotalOffer] ✅ Product removed');
      
      // Refresh cart UI
      await this.refreshCartUI();
      
    } catch (error) {
      console.error('[CartTotalOffer] ❌ Error removing product:', error);
    }
  }

  /**
   * Refresh cart UI
   */
  async refreshCartUI() {
    try {
      const global = window.CartTotalOfferGlobal;
      
      // Flag should already be set by caller, but ensure it's true
      if (!global.isRefreshing) {
        global.isRefreshing = true;
        console.log('[CartTotalOffer] 🔄 Setting refresh flag (fallback)');
      }
      
      const cart = await fetch('/cart.js').then(r => r.json());
      
      if (window.location.href.indexOf('cart') > -1) {
        window.location.reload();
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cart,
            openMiniCart: true,
            openDrawer: false
          }
        }));
        
        // Clear refresh flag after cart drawer has re-rendered
        setTimeout(() => {
          global.isRefreshing = false;
          console.log('[CartTotalOffer] ✅ Clearing refresh flag');
        }, 1000);
      }
    } catch (error) {
      console.error('[CartTotalOffer] ❌ Error refreshing cart:', error);
      window.CartTotalOfferGlobal.isRefreshing = false;
    }
  }

  /**
   * Dispatch offer update event for UI
   */
  dispatchOfferUpdateEvent(cartTotal, threshold, hasProduct) {
    const event = new CustomEvent('cart-offer:updated', {
      bubbles: true,
      detail: {
        cartTotal,
        threshold,
        hasProduct,
        remainingAmount: Math.max(0, threshold - cartTotal),
        progressPercentage: Math.min((cartTotal / threshold) * 100, 100),
        qualified: cartTotal >= threshold,
        offer: this.offerConfig
      }
    });
    
    document.dispatchEvent(event);
    console.log('[CartTotalOffer] 📤 Dispatched offer update event');
  }

  /**
   * Ensure loading indicator exists in DOM (create once, persists across cart refreshes)
   */
  ensureLoadingIndicator() {
    // Only create once globally
    if (document.getElementById('cart-total-offer-loading')) {
      return;
    }

    console.log('[CartTotalOffer] 🎨 Creating loading indicator');

    // Create loading indicator HTML
    const loadingDiv = document.createElement('div');
    loadingDiv.id = 'cart-total-offer-loading';
    loadingDiv.style.display = 'none';
    loadingDiv.innerHTML = `
      <div class="cart-offer-loader">
        <div class="cart-offer-loader-content">
          <svg class="spinner" width="40" height="40" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <circle class="spinner-circle" cx="12" cy="12" r="10" fill="none" stroke-width="2"></circle>
          </svg>
          <p class="cart-offer-loader-text">Updating cart...</p>
        </div>
      </div>
    `;

    // Inject styles
    const style = document.createElement('style');
    style.textContent = `
      #cart-total-offer-loading {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        border-radius: inherit;
      }

      .cart-offer-loader {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }

      .cart-offer-loader-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        background: white;
        padding: 24px 32px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .cart-offer-loader-text {
        margin: 0;
        font-size: 14px;
        color: #333;
        font-weight: 500;
      }

      .spinner {
        animation: rotate 1s linear infinite;
      }

      .spinner-circle {
        stroke: #3498db;
        stroke-linecap: round;
        animation: dash 1.5s ease-in-out infinite;
      }

      @keyframes rotate {
        100% {
          transform: rotate(360deg);
        }
      }

      @keyframes dash {
        0% {
          stroke-dasharray: 1, 150;
          stroke-dashoffset: 0;
        }
        50% {
          stroke-dasharray: 90, 150;
          stroke-dashoffset: -35;
        }
        100% {
          stroke-dasharray: 90, 150;
          stroke-dashoffset: -124;
        }
      }
    `;

    // Append to cart drawer (position: absolute relative to drawer)
    const cartDrawer = document.getElementById('mini-cart');
    if (cartDrawer) {
      // Ensure cart drawer has position relative
      if (getComputedStyle(cartDrawer).position === 'static') {
        cartDrawer.style.position = 'relative';
      }
      cartDrawer.appendChild(loadingDiv);
      cartDrawer.appendChild(style);
      console.log('[CartTotalOffer] ✅ Loading indicator appended to cart drawer');
    } else {
      // Fallback to body if cart drawer not found
      document.body.appendChild(loadingDiv);
      document.body.appendChild(style);
      console.log('[CartTotalOffer] ⚠️ Cart drawer not found, appended to body');
    }
  }

  /**
   * Show loading indicator
   */
  showLoading() {
    const global = window.CartTotalOfferGlobal;
    
    // Prevent multiple show calls
    if (global.loadingVisible) {
      return;
    }

    const loader = document.getElementById('cart-total-offer-loading');
    if (loader) {
      loader.style.display = 'flex';
      global.loadingVisible = true;
      console.log('[CartTotalOffer] 👁️ Loading visible');
    }
  }

  /**
   * Hide loading indicator
   */
  hideLoading() {
    const global = window.CartTotalOfferGlobal;
    const loader = document.getElementById('cart-total-offer-loading');
    
    if (loader && global.loadingVisible) {
      loader.style.display = 'none';
      global.loadingVisible = false;
      console.log('[CartTotalOffer] 👁️ Loading hidden');
    }
  }
}

// Register custom element
if (!customElements.get('cart-total-offer-manager')) {
  customElements.define('cart-total-offer-manager', CartTotalOfferManager);
  console.log('[CartTotalOffer] 🎨 Component registered ✓');
}

